import { z } from "zod";

export const ingredientCategorySchema = z.enum([
  "dairy",
  "stabilizer",
  "other",
  "egg",
  "emulsifier",
  "sweetener",
  "fruit",
  "chocolate",
  "nut",
  "alcohol",
]);

export const ingredientFractionSchema = z.number().finite().min(0).max(1);

/** Canonical normalized values: fractions in g/g, energy in kcal/g. */
export const ingredientCompositionFields = {
  sugarsPct: ingredientFractionSchema.default(0),
  lactosePct: ingredientFractionSchema.default(0),
  fatPct: ingredientFractionSchema.default(0),
  saturatedFatPct: ingredientFractionSchema.default(0),
  transFatPct: ingredientFractionSchema.default(0),
  dairyFatPct: ingredientFractionSchema.default(0),
  cocoaFatPct: ingredientFractionSchema.default(0),
  cocoaSolidsNonfatPct: ingredientFractionSchema.default(0),
  waterPct: ingredientFractionSchema.default(0),
  pod: z.number().finite().nonnegative().default(0),
  pac: z.number().finite().default(0),
  alcoholPct: ingredientFractionSchema.default(0),
  energyKcal: z.number().finite().nonnegative().default(0),
  proteinPct: ingredientFractionSchema.default(0),
  carbPct: ingredientFractionSchema.default(0),
  fiberPct: ingredientFractionSchema.default(0),
  emulsifierPct: ingredientFractionSchema.default(0),
  stabilizerPct: ingredientFractionSchema.default(0),
};

export const ingredientNumericFields = z
  .object(ingredientCompositionFields)
  .keyof().options;
export type IngredientNumericField = keyof typeof ingredientCompositionFields;

/** Shared by API writes and the calculator's creation/preparation pipeline. */
export const ingredientWriteSchema = z
  .object({
    name: z.string().trim().min(1, "Name is required"),
    notes: z.string().default(""),
    aliases: z.array(z.string()).default([]),
    category: ingredientCategorySchema.default("other"),
    ...ingredientCompositionFields,
  })
  .superRefine((input, context) => {
    // Allow floating-point rounding when display percentages become fractions.
    const compositionTolerance = 1e-10;
    if (
      input.waterPct + input.fatPct + input.sugarsPct + input.alcoholPct >
      1 + compositionTolerance
    ) {
      context.addIssue({
        code: "custom",
        message: "Water, fat, sugars, and alcohol cannot exceed 100%",
        path: ["waterPct"],
      });
    }
  });

export const ingredientSugarBreakdownSchema = z.object({
  sucrose: ingredientFractionSchema,
  glucose: ingredientFractionSchema,
  fructose: ingredientFractionSchema,
  lactose: ingredientFractionSchema,
  maltose: ingredientFractionSchema,
  galactose: ingredientFractionSchema,
});

/** Adapters choose which derivation inputs are required at their boundary. */
export const ingredientDerivationFields = {
  sugarBreakdown: ingredientSugarBreakdownSchema,
  saltPct: ingredientFractionSchema,
  sodiumMgPer100g: z.number().finite().min(0).max(100_000),
  ashPct: ingredientFractionSchema,
  waterByDifference: z.boolean(),
};

export const ingredientDerivationSchema = z
  .object({
    ...ingredientDerivationFields,
    sugarBreakdown: ingredientSugarBreakdownSchema.partial(),
  })
  .partial()
  .refine((data) => !data.waterByDifference || data.ashPct !== undefined, {
    path: ["ashPct"],
    message: "Ash fraction is required when deriving water by difference.",
  });

export type IngredientCategory = z.infer<typeof ingredientCategorySchema>;
export type IngredientWrite = z.input<typeof ingredientWriteSchema>;
export type ParsedIngredientWrite = z.output<typeof ingredientWriteSchema>;
export type IngredientDerivation = z.output<typeof ingredientDerivationSchema>;
